import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="nested-tasks"
export default class extends Controller {
    static targets = ['container', 'template']

    connect() {
        if (!this.hasContainerTarget) {
            console.warn('nested-tasks: container target missing')
        }
        if (!this.hasTemplateTarget) {
            // fallback: try to find the template element inside the controller root
            this._templateFallback = this.element.querySelector('[data-nested-tasks-target="template"]')
            if (this._templateFallback) {
                console.info('nested-tasks: found template via fallback')
            } else {
                console.warn('nested-tasks: template target missing')
            }
        }

        this._form = this.element.closest('form')
        if (this._form) {
            this._submitHandler = this._validateOnSubmit.bind(this)
            this._form.addEventListener('submit', this._submitHandler)
        }
    }

    disconnect() {
        if (this._form && this._submitHandler) {
            this._form.removeEventListener('submit', this._submitHandler)
        }
    }

    _validateOnSubmit(event) {
        const rows = this.element.querySelectorAll('.nested-task')
        let firstInvalid = null

        rows.forEach(row => {
            if (row.style.display === 'none') return // soft-deleted

            const descriptionInput = row.querySelector('input[name*="[description]"]')
            const durationInput = row.querySelector('[data-duration-field]')
            const minutesInput = row.querySelector('[data-minutes-field]')
            if (!durationInput) return

            // Blank rows (no description) are ignored server-side — skip them
            if (!descriptionInput?.value.trim()) return

            const minutes = parseInt(minutesInput?.value || '0', 10)
            const empty = !durationInput.value.trim() || minutes <= 0

            durationInput.classList.toggle('border-red-400', empty)
            durationInput.classList.toggle('dark:border-red-600', empty)
            durationInput.classList.toggle('border-transparent', !empty)
            durationInput.classList.toggle('bg-red-50', empty)
            durationInput.classList.toggle('dark:bg-red-950', empty)
            durationInput.classList.toggle('bg-zinc-100', !empty)
            durationInput.classList.toggle('dark:bg-zinc-700', !empty)

            let errorEl = row.querySelector('.duration-error')
            let hintEl = row.querySelector('.duration-hint')

            if (empty) {
                if (!errorEl) {
                    errorEl = document.createElement('p')
                    errorEl.className = 'text-xs text-red-500 dark:text-red-400 mt-0.5 duration-error'
                    errorEl.textContent = durationInput.dataset.errorMessage || 'Duration is required'
                    durationInput.insertAdjacentElement('afterend', errorEl)
                }
                if (hintEl) hintEl.style.display = 'none'
                if (!firstInvalid) firstInvalid = durationInput
            } else {
                if (errorEl) errorEl.remove()
                if (hintEl) hintEl.style.display = ''
            }
        })

        if (firstInvalid) {
            event.preventDefault()
            firstInvalid.focus()
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }
    addFirst(event) {
        event.preventDefault()

        const container = this.hasContainerTarget
            ? this.containerTarget
            : this.element.querySelector('[data-nested-tasks-target="container"]')

        const templateEl = this.hasTemplateTarget
            ? this.templateTarget
            : this._templateFallback ||
            this.element.querySelector('[data-nested-tasks-target="template"]')

        if (!container || !templateEl) return

        const content = templateEl.innerHTML.replace(/NEW_RECORD/g, Date.now())
        container.insertAdjacentHTML('afterbegin', content)
        const newTask = container.firstElementChild
        const descriptionField = newTask?.querySelector('input[name*="[description]"]')
        if (descriptionField) descriptionField.focus()
        this.element.dispatchEvent(new CustomEvent('tasks-changed', { bubbles: true }))
    }

    add(event) {
        event.preventDefault()

        const container = this.hasContainerTarget
            ? this.containerTarget
            : this.element.querySelector('[data-nested-tasks-target="container"]')

        const templateEl = this.hasTemplateTarget
            ? this.templateTarget
            : this._templateFallback ||
            this.element.querySelector('[data-nested-tasks-target="template"]')

        if (!container) {
            console.error('nested-tasks: cannot add - container not found')
            return
        }
        if (!templateEl) {
            console.error('nested-tasks: cannot add - template not found')
            return
        }

        const rawHtml =
            templateEl instanceof HTMLTemplateElement
                ? templateEl.innerHTML
                : templateEl.innerHTML

        const content = rawHtml.replace(/NEW_RECORD/g, Date.now())
        container.insertAdjacentHTML('beforeend', content)
        const newTask = container.lastElementChild
        const descriptionField = newTask?.querySelector('input[name*="[description]"]')
        if (descriptionField) descriptionField.focus()
        this.element.dispatchEvent(new CustomEvent('tasks-changed', { bubbles: true }))
    }

    addAfter(event) {
        event.preventDefault()

        const templateEl = this.hasTemplateTarget
            ? this.templateTarget
            : this._templateFallback ||
            this.element.querySelector('[data-nested-tasks-target="template"]')

        if (!templateEl) {
            console.error('nested-tasks: cannot add - template not found')
            return
        }

        const currentRow = event.currentTarget.closest('.nested-task')
        if (!currentRow) return

        const content = templateEl.innerHTML.replace(/NEW_RECORD/g, Date.now())
        currentRow.insertAdjacentHTML('afterend', content)

        const newTask = currentRow.nextElementSibling
        const descriptionField = newTask?.querySelector('input[name*="[description]"]')
        if (descriptionField) descriptionField.focus()

        this.element.dispatchEvent(new CustomEvent('tasks-changed', { bubbles: true }))
    }

    remove(event) {
        event.preventDefault()
        const btn = event.currentTarget
        const wrapper = btn.closest('.nested-task')
        if (!wrapper) return

        const destroyField = wrapper.querySelector('input[name*="_destroy"]')
        if (destroyField) {
            destroyField.value = '1'
            wrapper.style.display = 'none'
        } else {
            wrapper.remove()
        }
        this.element.dispatchEvent(new CustomEvent('tasks-changed', { bubbles: true }))
    }
}
